import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  LinearProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import {
  CloudUpload,
  Email,
  Description,
  Delete,
  Add,
  Close,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import CaseAutocomplete, { CaseAutocompleteOption } from '../Common/CaseAutocomplete';

interface DocumentUploadProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Fixed matter (uploading from inside a case) — hides the matter picker. */
  caseId?: string;
  clientId?: string;
  /** When true, "Save as Template" defaults on (e.g. Firm Templates page). */
  defaultIsTemplate?: boolean;
  /** Dialog heading override, e.g. "Upload Document to Case Folder". */
  title?: string;
}

interface UploadFile {
  file: File;
  id: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
}

interface DocumentMetadata {
  title: string;
  description: string;
  documentType: string;
  category: string;
  tags: string[];
  isConfidential: boolean;
  isPublic: boolean;
  isTemplate: boolean;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  open,
  onClose,
  onSuccess,
  caseId,
  clientId,
  defaultIsTemplate = false,
  title: dialogTitle,
}) => {
  const { token } = useAuth();
  const [files, setFiles] = useState<UploadFile[]>([]);
  // Only offered when the dialog was not opened from inside a matter.
  const [selectedCase, setSelectedCase] = useState<CaseAutocompleteOption | null>(null);
  const [metadata, setMetadata] = useState<DocumentMetadata>({
    title: '',
    description: '',
    documentType: 'other',
    category: '',
    tags: [],
    isConfidential: false,
    isPublic: false,
    isTemplate: defaultIsTemplate,
  });
  const [newTag, setNewTag] = useState('');
  const [showEmailImport, setShowEmailImport] = useState(false);
  const [emailData, setEmailData] = useState({
    emailSubject: '',
    emailFrom: '',
    emailDate: '',
    attachmentName: '',
    attachmentData: '',
  });

  useEffect(() => {
    if (open) {
      setMetadata((prev) => ({ ...prev, isTemplate: defaultIsTemplate }));
      setSelectedCase(null);
    }
  }, [open, defaultIsTemplate]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles: UploadFile[] = acceptedFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      progress: 0,
      status: 'pending',
    }));
    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff'],
      'video/*': ['.mp4', '.avi', '.mov'],
      'audio/*': ['.mp3', '.wav'],
      'application/zip': ['.zip'],
      'text/plain': ['.txt'],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: true,
  });

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(file => file.id !== id));
  };

  const addTag = () => {
    if (newTag.trim() && !metadata.tags.includes(newTag.trim())) {
      setMetadata(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setMetadata(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove),
    }));
  };

  const uploadFiles = async () => {
    if (files.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    // A matter chosen in the dialog behaves exactly like being opened from inside one.
    const targetCaseId = caseId || selectedCase?.id || '';

    const formData = new FormData();
    files.forEach(file => {
      formData.append('documents', file.file);
    });

    // Add metadata
    Object.entries(metadata).forEach(([key, value]) => {
      if (key === 'isTemplate') {
        formData.append(key, (value === true && !targetCaseId).toString());
      } else if (key === 'tags') {
        formData.append('tags', JSON.stringify(value));
      } else {
        formData.append(key, value.toString());
      }
    });

    if (targetCaseId) formData.append('caseId', targetCaseId);
    const targetClientId = clientId || (caseId ? undefined : selectedCase?.clientId || selectedCase?.client?.id);
    if (targetClientId) formData.append('clientId', targetClientId);

    try {
      // Use case-documents endpoint if a matter is in play, otherwise the library endpoint
      const endpoint = targetCaseId ? `/api/case-documents/${targetCaseId}/documents` : '/api/documents/upload';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message);
        setFiles([]);
        setMetadata({
          title: '',
          description: '',
          documentType: 'other',
          category: '',
          tags: [],
          isConfidential: false,
          isPublic: false,
          isTemplate: defaultIsTemplate,
        });
        setSelectedCase(null);
        onSuccess?.();
        onClose();
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
      console.error('Upload error:', error);
    }
  };

  const importEmailAttachment = async () => {
    if (!emailData.attachmentData) {
      toast.error('Please provide attachment data');
      return;
    }

    try {
      const response = await fetch('/api/documents/import-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...emailData,
          caseId,
          clientId,
          documentType: 'email_attachment',
          category: metadata.category,
          tags: metadata.tags,
          isConfidential: metadata.isConfidential,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message);
        setShowEmailImport(false);
        setEmailData({
          emailSubject: '',
          emailFrom: '',
          emailDate: '',
          attachmentName: '',
          attachmentData: '',
        });
        onSuccess?.();
        onClose();
      } else {
        toast.error(data.message || 'Import failed');
      }
    } catch (error) {
      toast.error('Import failed');
      console.error('Import error:', error);
    }
  };

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Data = e.target?.result as string;
        setEmailData(prev => ({
          ...prev,
          attachmentData: base64Data.split(',')[1], // Remove data URL prefix
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {dialogTitle || 'Upload Documents'}
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* File Upload Area */}
        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.300',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
            mb: 2,
          }}
        >
          <input {...getInputProps()} />
          <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            or click to select files
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Supported formats: PDF, DOC, DOCX, XLS, XLSX, Images, Videos, Audio
          </Typography>
        </Box>

        {/* Email Import Button */}
        <Button
          variant="outlined"
          startIcon={<Email />}
          onClick={() => setShowEmailImport(true)}
          sx={{ mb: 2 }}
        >
          Import Email Attachment
        </Button>

        {/* Selected Files */}
        {files.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Selected Files ({files.length})
            </Typography>
            {files.map((file) => (
              <Box
                key={file.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <Description sx={{ mr: 1 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2">{file.file.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.file.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => removeFile(file.id)}
                  color="error"
                >
                  <Delete />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {/* Metadata Form */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {!caseId && (
            <Grid item xs={12}>
              <CaseAutocomplete
                value={selectedCase}
                onChange={setSelectedCase}
                label="Assign to Case (optional)"
                placeholder="Search by case number, title or client"
                size="small"
                textFieldProps={{
                  helperText: selectedCase
                    ? 'These files will be filed in this matter\'s folder.'
                    : 'Leave empty to keep the files unfiled; you can assign them to a matter later.',
                }}
              />
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Title"
              value={metadata.title}
              onChange={(e) => setMetadata(prev => ({ ...prev, title: e.target.value }))}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Document Type</InputLabel>
              <Select
                value={metadata.documentType}
                onChange={(e) => setMetadata(prev => ({ ...prev, documentType: e.target.value }))}
                label="Document Type"
              >
                <MenuItem value="pleading">Pleading</MenuItem>
                <MenuItem value="discovery">Discovery</MenuItem>
                <MenuItem value="correspondence">Correspondence</MenuItem>
                <MenuItem value="evidence">Evidence</MenuItem>
                <MenuItem value="court_filing">Court Filing</MenuItem>
                <MenuItem value="contract">Contract</MenuItem>
                <MenuItem value="agreement">Agreement</MenuItem>
                <MenuItem value="invoice">Invoice</MenuItem>
                <MenuItem value="receipt">Receipt</MenuItem>
                <MenuItem value="id_document">ID Document</MenuItem>
                <MenuItem value="medical_record">Medical Record</MenuItem>
                <MenuItem value="police_report">Police Report</MenuItem>
                <MenuItem value="expert_report">Expert Report</MenuItem>
                <MenuItem value="witness_statement">Witness Statement</MenuItem>
                <MenuItem value="photograph">Photograph</MenuItem>
                <MenuItem value="video">Video</MenuItem>
                <MenuItem value="audio">Audio</MenuItem>
                <MenuItem value="email_attachment">Email Attachment</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={metadata.description}
              onChange={(e) => setMetadata(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={3}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Category"
              value={metadata.category}
              onChange={(e) => setMetadata(prev => ({ ...prev, category: e.target.value }))}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                label="Add Tag"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addTag()}
                size="small"
              />
              <Button onClick={addTag} variant="outlined" size="small">
                <Add />
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {metadata.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => removeTag(tag)}
                  size="small"
                />
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={metadata.isConfidential}
                  onChange={(e) => setMetadata(prev => ({ ...prev, isConfidential: e.target.checked }))}
                />
              }
              label="Confidential"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={metadata.isPublic}
                  onChange={(e) => setMetadata(prev => ({ ...prev, isPublic: e.target.checked }))}
                />
              }
              label="Public"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={metadata.isTemplate && !caseId && !selectedCase}
                  disabled={Boolean(caseId || selectedCase)}
                  onChange={(e) => setMetadata(prev => ({ ...prev, isTemplate: e.target.checked }))}
                />
              }
              label={caseId || selectedCase ? 'Save as Template (not available for matter files)' : 'Save as Template'}
            />
          </Grid>
        </Grid>

        {/* Upload Button */}
        <Button
          variant="contained"
          onClick={uploadFiles}
          disabled={files.length === 0}
          fullWidth
        >
          Upload {files.length} Document{files.length !== 1 ? 's' : ''}
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={uploadFiles}
          disabled={files.length === 0}
        >
          Upload {files.length} Document{files.length !== 1 ? 's' : ''}
        </Button>
      </DialogActions>

        {/* Email Import Dialog */}
        <Dialog open={showEmailImport} onClose={() => setShowEmailImport(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Import Email Attachment
            <IconButton
              onClick={() => setShowEmailImport(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email Subject"
                  value={emailData.emailSubject}
                  onChange={(e) => setEmailData(prev => ({ ...prev, emailSubject: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="From"
                  value={emailData.emailFrom}
                  onChange={(e) => setEmailData(prev => ({ ...prev, emailFrom: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email Date"
                  type="datetime-local"
                  value={emailData.emailDate}
                  onChange={(e) => setEmailData(prev => ({ ...prev, emailDate: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Attachment Name"
                  value={emailData.attachmentName}
                  onChange={(e) => setEmailData(prev => ({ ...prev, attachmentName: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <input
                  type="file"
                  onChange={handleFileInput}
                  style={{ display: 'none' }}
                  id="email-attachment-input"
                />
                <label htmlFor="email-attachment-input">
                  <Button variant="outlined" component="span" fullWidth>
                    Select Attachment File
                  </Button>
                </label>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowEmailImport(false)}>Cancel</Button>
            <Button onClick={importEmailAttachment} variant="contained">
              Import Attachment
            </Button>
          </DialogActions>
        </Dialog>
    </Dialog>
  );
};

export default DocumentUpload; 